import React, { useState, useEffect, useRef } from 'react'
import { Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { IQuestionData, QuestionForm } from 'models/question.model'
import * as fromEvaluationActions from 'store/actions/evaluation-questions.action'
import * as fromQuestionActions from 'store/actions/questions.action'
import { IMainState } from 'store/states'
import Loader, { LoaderSizes } from 'components/ui/loader/Loader'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import EvaluationQuestionsEditor from './components/evaluation-question-editor/EvaluationQuestionsEditor'
import EvaluationQuestionCard from './components/evaluation-question-card/EvaluationQuestionCard'
import styles from './EvaluationQuestions.module.scss'
import { ConfirmationEditQuestion } from './components/ConfirmationEditQuestion'

interface IEvaluationQuestions {
  id: number
  instanceId: number
  onAnyChange(): void
}

const EvaluationQuestions: React.FC<IEvaluationQuestions> = ({
  id,
  instanceId,
  onAnyChange,
}) => {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(0)
  const [questions, setQuestions] = useState<IQuestionData[]>([])
  const [confirmationCallback, setConfirmationCallbackBase] = useState<
    (() => void) | null
  >(null)
  const confirmed = useRef(false)
  const { edit } = useSelector(({ blocks }: IMainState) => blocks)

  const setConfirmationCallback = (callback: () => void) => {
    if (confirmed.current || !edit.hasResponses) return callback()
    setConfirmationCallbackBase(() => () => callback())
  }

  const dispatch = useDispatch()
  const { list, loading } = useSelector(
    ({ questions }: IMainState) => questions
  )

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const [selected, setSelected] = useState(0)

  useEffect(() => {
    dispatch({ type: fromQuestionActions.GET_QUESTIONS_LIST, instanceId })
  }, [dispatch, instanceId])

  useEffect(() => {
    setQuestions(list)
  }, [list])

  const questionCount = questions.length + 1

  const handleCreate = (questionData: QuestionForm) => {
    const { order } = questionData
    const prevQuestion = (order && questions[order - 1]) || null
    const result = {
      ...questionData,
      order:
        prevQuestion && prevQuestion.order >= 0 ? prevQuestion.order + 1 : 0,
    }
    onAnyChange()
    dispatch(
      fromEvaluationActions.Actions.questionLikertCreate({
        id,
        questionData: result,
        instanceId,
      })
    )
  }

  const handleEdit = (questionId: number, questionData: QuestionForm) => {
    setConfirmationCallback(() => {
      onAnyChange()
      dispatch(
        fromEvaluationActions.Actions.questionLikertEdit({
          questionId,
          questionData,
          id,
          instanceId,
        })
      )
      setEditing(0)
    })
  }

  const handleConfirmationDelete = (questionId: number) => {
    setConfirmationCallback(() => {
      setSelected(questionId)
      setShowDeleteConfirmationModal(true)
    })
  }

  const handleDelete = (questionId: number) => {
    dispatch({
      type: fromEvaluationActions.EVALUATION_QUESTION_LIKERT_DESTROY,
      data: { questionId, instanceId },
    })
    setEditing(0)
    setSelected(0)
    setShowDeleteConfirmationModal(false)
    onAnyChange()
  }
  return (
    <div className={styles.content}>
      <Row>
        <h1 className={styles.title}>
          {t('administration.blocks.type.evaluation.titles.questions')}
        </h1>
      </Row>
      <Row>
        {loading ? (
          <Loader sizes={LoaderSizes.ExtraSmall} color />
        ) : (
          questions.map((question, index) =>
            editing === question.questionableId ? (
              <EvaluationQuestionsEditor
                key={question.questionableId + '-' + index}
                editing={editing}
                onDiscardChanges={() => setEditing(0)}
                onFinish={(data) => handleEdit(question.questionableId, data)}
                question={question}
                number={index + 1}
                setConfirmationCallback={setConfirmationCallback}
              />
            ) : (
              <EvaluationQuestionCard
                key={question.questionableId + '-' + index}
                question={question}
                onEdit={() => {
                  setEditing(question.questionableId)
                }}
                onDelete={() => {
                  handleConfirmationDelete(question.questionableId)
                }}
                number={index + 1}
              />
            )
          )
        )}
      </Row>
      {!editing && (
        <Row>
          <EvaluationQuestionsEditor
            onCreate={handleCreate}
            editing={editing}
            number={questionCount}
            setConfirmationCallback={setConfirmationCallback}
          />
        </Row>
      )}

      <ConfirmationModal
        show={showDeleteConfirmationModal}
        title=""
        text={t('administration.modal.deleteQuestionConfirmation')}
        acceptButtonText={t('components.modal.buttons.delete')}
        onAccept={() => handleDelete(selected)}
        onClose={() => setShowDeleteConfirmationModal(false)}
      />
      <ConfirmationEditQuestion
        show={!!confirmationCallback}
        onAccept={() => {
          confirmed.current = true
          confirmationCallback?.()
          setConfirmationCallbackBase(null)
        }}
        onClose={() => {
          setConfirmationCallbackBase(null)
        }}
      />
    </div>
  )
}

export default EvaluationQuestions
